import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getDisposition } from './Procedure';
import { Procedure as ProcedureT, Procedure } from '../../../../../types/Procedure';
import { differenceInHours, format, getHours, setHours, setMinutes, setSeconds } from 'date-fns';
import { currentDateInZone } from '../../../../../util/dateTime';
import { useScope } from '@/hooks/useScope';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { Room } from '@/types/Room';
import OperatingRoomProcedures from './OperatingRoomProcedures';
import { Time } from '../../../../entities/schedule/util/time';
import isEmpty from 'lodash/fp/isEmpty';
import Root from './components/Root';
import Tick from './components/Tick';
import Grid from './components/Grid';
import XAxis from './components/XAxis';
import YAxis from './components/YAxis';
import Rooms from './components/Rooms';
import Cursor from './components/Cursor';
import Dot from './components/Dot';
import { TimelineRoom } from '@/components/pages/kiosk/schedule/timeline/TimelineRoom';

const Timeline: FC<{
  currentOR?: string;
  isKiosk?: boolean;
  isFrontDesk?: boolean;
  editableStaff?: boolean;
  operationRooms: Room[];
  canUseScheduleViewProcedure?: boolean;
  isPowerUser?: boolean;
  date: Date;
  setEditProcedure?: Dispatch<SetStateAction<ProcedureT | null>>;
  showBedNumber?: boolean;
  scheduleStaffList?: any;
  showOverlay: boolean;
  editMode: boolean;
  openProcedureForm?: () => void;
  startTime: Time;
  duration: number | null | undefined;
  setStartTime?: (val: Time) => void;
  setDuration?: (val: number | null | undefined) => void;
  condensView?: boolean;
  unassignedProcedures: Procedure[];
  hasMoreThan6ORsView: boolean;
  showGraphs?: boolean;
  isAnesthesiologistSchedule?: boolean;
  secondaryRoomAnesthesiologist?: boolean;
  autoDischarge?: boolean;
}> = ({
  currentOR,
  isFrontDesk,
  isKiosk,
  editableStaff,
  operationRooms,
  canUseScheduleViewProcedure,
  isPowerUser,
  date,
  setEditProcedure,
  showBedNumber,
  scheduleStaffList,
  showOverlay = false,
  editMode,
  openProcedureForm,
  startTime,
  duration,
  setStartTime,
  setDuration,
  condensView,
  unassignedProcedures,
  hasMoreThan6ORsView,
  showGraphs,
  isAnesthesiologistSchedule,
  secondaryRoomAnesthesiologist,
  autoDischarge,
}) => {
  const dateKey = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scope = useScope();
  const tz = scope?.hospital.timezone.id;
  // Keep a ticking "now" time for cursor without recomputing on every parent render
  const [now, setNow] = useState<Date>(() => currentDateInZone(tz));
  useEffect(() => {
    // Initialize immediately and then update on an interval
    setNow(currentDateInZone(tz));
    const id = setInterval(() => setNow(currentDateInZone(tz)), 15_000);
    return () => clearInterval(id);
  }, [tz]);
  const nowKey = useMemo(() => format(now, 'YYYY-MM-DD'), [now]);
  const isViewingToday = dateKey === nowKey;

  // We intentionally key by `dateKey` to recompute only when the calendar day changes,
  // not when the `Date` instance identity changes. Suppress exhaustive-deps accordingly.
  const gridForDate = useMemo(() => {
    const ds = setSeconds(setMinutes(setHours(date, 6), 0), 0);
    const de = setSeconds(setMinutes(setHours(date, 19), 0), 0);
    const hourCount = differenceInHours(de, ds);
    const startHour = getHours(ds);
    const hours = [...new Array(hourCount + 1)].map((_, i) => `${i + startHour}:00`);
    return { dayStart: ds, dayEnd: de, hours };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey]);

  // Same rationale as above: recompute when the current day string changes.
  const gridForToday = useMemo(() => {
    const ds = setSeconds(setMinutes(setHours(now, 6), 0), 0);
    const de = setSeconds(setMinutes(setHours(now, 19), 0), 0);
    const hourCount = differenceInHours(de, ds);
    const startHour = getHours(ds);
    const hours = [...new Array(hourCount + 1)].map((_, i) => `${i + startHour}:00`);
    return { dayStart: ds, dayEnd: de, hours };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowKey]);

  const { dayStart, dayEnd, hours } = isViewingToday ? gridForToday : gridForDate;
  const editableAnesthesiologist = !isKiosk && !isFrontDesk && Boolean(editableStaff);

  const hasMoreThan6ORs = isKiosk ? false : operationRooms.length + (!isEmpty(unassignedProcedures) ? 1 : 0) > 6;

  const operationRoomsToRender = useMemo(() => {
    if (!isMobile) return operationRooms;
    if (currentOR) return operationRooms.filter(room => room.name === currentOR);
    return operationRooms.length ? [operationRooms[0]] : [];
  }, [currentOR, isMobile, operationRooms]);

  const timelineContent = (
    <Root isKiosk={isKiosk} hasMoreThan6ORs={hasMoreThan6ORs} hasMoreThan6ORsView={hasMoreThan6ORsView}>
      <Rooms
        columns={isMobile ? 1 : operationRooms.length}
        hasMoreThan6ORs={hasMoreThan6ORs}
        hasMoreThan6ORsView={hasMoreThan6ORsView}
      >
        {operationRoomsToRender.map(room => {
          const { id, name, procedures, staffShifts, anesthesiologistShifts, secondaryAnesthesiologistShifts, type } =
            room;

          return (
            <TimelineRoom
              key={`room-${id}`}
              id={id}
              name={name}
              type={type}
              staffShifts={staffShifts || []}
              anesthesiologistShifts={anesthesiologistShifts || []}
              secondaryAnesthesiologistShifts={secondaryAnesthesiologistShifts || []}
              procedures={procedures}
              editableAnesthesiologist={editableAnesthesiologist}
              condensView={Boolean(condensView)}
              isAnesthesiologistSchedule={Boolean(isAnesthesiologistSchedule)}
              isKiosk={Boolean(isKiosk)}
              editableStaff={Boolean(editableStaff)}
              date={date}
            />
          );
        })}
      </Rooms>
      <Grid isKiosk={isKiosk} hasMoreThan6ORs={hasMoreThan6ORs} hasMoreThan6ORsView={hasMoreThan6ORsView}>
        <YAxis>
          {hours.map(tick => (
            <Tick key={tick}>{tick}</Tick>
          ))}
        </YAxis>
        <Cursor y={getDisposition(dayStart, now, dayEnd)}>
          <Dot />
        </Cursor>
        <XAxis>
          {operationRoomsToRender.map((room, i) => (
            <OperatingRoomProcedures
              key={room?.id || room?.name}
              operationRooms={operationRooms}
              index={i}
              date={date}
              room={room}
              currentOR={currentOR}
              isKiosk={isKiosk}
              canUseScheduleViewProcedure={canUseScheduleViewProcedure}
              isFrontDesk={isFrontDesk}
              isPowerUser={isPowerUser}
              editableStaff={editableStaff}
              showOverlay={showOverlay}
              editMode={editMode}
              showBedNumber={showBedNumber}
              setEditProcedure={setEditProcedure}
              dayStart={dayStart}
              dayEnd={dayEnd}
              openProcedureForm={openProcedureForm}
              startTime={startTime}
              duration={duration}
              setStartTime={setStartTime}
              setDuration={setDuration}
              anesthesiologistSchedule={isAnesthesiologistSchedule}
            />
          ))}
        </XAxis>
      </Grid>
    </Root>
  );

  if (isKiosk && scheduleStaffList) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: scheduleStaffList ? 'row' : 'column',
          width: '100%',
          height: '100%',
        }}
      >
        {timelineContent}
        {scheduleStaffList}
      </div>
    );
  }
  return timelineContent;
};

export default Timeline;
